import React, { useState } from 'react';
import styled from 'styled-components';
import {
    Person, Star, CalendarToday, School, Work, EmojiEvents,
    Security, ThumbUp, CheckCircle, ExpandMore, ExpandLess, Spa
} from '@mui/icons-material';
import {
    Button,
    Typography,
    Paper,
    Avatar,
    Grid,
    Alert,
    AlertTitle,
    Box,
} from '@mui/material';
import styles from "../../pages/hygienist/ShiftDetailPage/ShiftDetail.module.scss";
import {CrossButtonBlack} from "../Buttons/CrossButton";
import clsx from "clsx";
import {HYGIENIST_TYPES, PositionProps, SkillsProps} from "utils/types";
import dayjs from "dayjs";
import {formatProfileHygienistName} from "../../utils";
import { formatFirstLicensedLabel } from "../../utils/educationCredentials";
import { Language } from 'api/Api';
import shiftHeroBadge from 'images/shift-hero/shift-hero-badge.png';
const relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

const StyledPaper = styled(Paper)`
    margin: 0 auto 3rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    overflow: auto;
    width: 100%;
    max-width: 720px;

    @media only screen and (min-width: 600px) {
        width: 90%;
    }
`;
const Section = styled(Paper)`
  margin-bottom: 1rem;
  padding: 1rem;
`;
interface ExpandButtonProps {
    onClick: () => void;
    icon: React.ReactNode;
    children: React.ReactNode;
    isExpanded: boolean;
    showChevron?: boolean;
}
const ExpandButton: React.FC<ExpandButtonProps> = ({ onClick, icon, children, isExpanded, showChevron = false }) => (
    <Button
        onClick={onClick}
        fullWidth
        sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'left',
            padding: '12px',
            backgroundColor: 'rgba(3, 169, 244, 0.1)',
            '&:hover': {
                backgroundColor: 'rgba(3, 169, 244, 0.2)',
            },
            borderRadius: '4px',
        }}
    >
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {icon}
        <span style={{ marginLeft: '8px', textTransform: 'none' }}>{children}</span>
    </span>
        {showChevron && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
    </Button>
);

const TalentResume: React.FC<{hygienist: any, onClose: () => void}> = ({hygienist, onClose}) => {
    const [isPrivacyExpanded, setIsPrivacyExpanded] = useState(false);
    const [isSkillsExpanded, setIsSkillsExpanded] = useState(true);
    const [isWorkExpanded, setIsWorkExpanded] = useState(true);
    const [isAboutExpanded, setIsAboutExpanded] = useState(true);
    const [isTempStarsHistoryExpanded, setIsTempStarsHistoryExpanded] = useState(true);

    if (!hygienist) {
        return (
            <StyledPaper elevation={3}>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', zIndex: 1}}>
                    <div></div>
                    <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
                </div>
                <Alert severity="warning">
                    Unable to load resume details for this professional.
                </Alert>
            </StyledPaper>
        )
    }

    const professionalLicense = formatProfileHygienistName(hygienist, hygienist?.skills);

    const now = dayjs();
    const createdDate = dayjs(hygienist?.createdOn);
    const years = now.diff(createdDate, 'year');
    const months = now.diff(createdDate.add(years, 'year'), 'month');

    let withTempStarsString = `${years}yrs ${months}mo with TempStars`;
    if (years === 0 && months === 0) {
        withTempStarsString = 'New to TempStars';
    }
    if (years === 0 && months > 0) {
        withTempStarsString = `${months}mo with TempStars`;
    }

    const status = hygienist?.membershipSummary?.officeStatusLabel || null;
    const statusCopy = status && ['Active', 'Pro', 'Elite'].includes(status)
        ? `TempStars ${status} Status`
        : status;

    const languages = hygienist?.languages ?? [];
    const languagesToShow = languages.filter((lang: Language) => lang.code !== 'en') ?? [];

    return (
        <StyledPaper elevation={3}>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', zIndex: 1}}>
                <div></div>
                <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
            </div>
            <Grid container spacing={2} alignItems="center" sx={{marginBottom: 3}}>
                <Grid item>
                    <Avatar src={hygienist?.photoUrl} alt={hygienist?.firstName || ''} sx={{width: 64, height: 64}}/>
                </Grid>
                <Grid item>
                    <Typography variant="h4" component="h1">{professionalLicense}</Typography>
                    <Typography variant="h5" color="textSecondary">{hygienist?.city}, {hygienist?.province}</Typography>
                </Grid>
            </Grid>
            <Section>
                <ExpandButton
                    onClick={() => setIsPrivacyExpanded(!isPrivacyExpanded)}
                    icon={<Security color="primary"/>}
                    isExpanded={isPrivacyExpanded}
                    showChevron={true}
                >
                    Why don't you see contact information?
                </ExpandButton>
                {isPrivacyExpanded && (
                    <Alert severity="info" sx={{marginTop: 2}}>
                        <AlertTitle>Privacy Notice</AlertTitle>
                        TempStars respects dental professionals' privacy and channels of communication.
                        While we don't display contact information on resumes, once you book a TempStars temp
                        for a shift,
                        you can use our robust messenger to communicate.
                        We have a full-time customer support team always on hand to help out and ensure things go smoothly!
                    </Alert>
                )}
            </Section>
            <Section>
                <Typography variant="h5" component="h3" sx={{display: 'flex', alignItems: 'center', marginBottom: 2}}>
                    <Person sx={{marginRight: 1}}/> For starters...
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography sx={{display: 'flex', alignItems: 'center'}}>
                            <School sx={{marginRight: 1, color: 'primary.main'}}/>
                            Graduated: {hygienist?.graduationYear}
                        </Typography>
                        {formatFirstLicensedLabel(hygienist?.firstLicensedYear, hygienist?.firstLicensedMonth) && (
                            <Typography variant="body2" color="textSecondary" sx={{marginLeft: 3}}>
                                {formatFirstLicensedLabel(hygienist?.firstLicensedYear, hygienist?.firstLicensedMonth)}
                            </Typography>
                        )}
                        {hygienist.school && <Typography variant="body2" color="textSecondary" sx={{marginLeft: 3}}>
                            School: {hygienist.school}
                        </Typography>}
                    </Grid>
                    <Grid item xs={6}>
                        {statusCopy && (
                            <Typography sx={{display: 'flex', alignItems: 'center'}}>
                                <Star sx={{marginRight: 1, color: 'warning.main'}}/>
                                {statusCopy}
                            </Typography>
                        )}
                    </Grid>
                    {!!(hygienist?.shiftHero ?? hygienist?.shiftSaviour) && (
                        <Grid item xs={12}>
                            <Typography
                                sx={{display: 'flex', alignItems: 'center', fontWeight: 700, color: '#0B4598'}}
                                data-testid="shift-hero-resume-label"
                                id="shift-hero-resume-label"
                            >
                                <Box
                                    component="img"
                                    src={shiftHeroBadge}
                                    alt=""
                                    sx={{ width: 22, height: 22, mr: 1, objectFit: 'contain', flexShrink: 0 }}
                                    aria-hidden
                                />
                                Shift Hero - known for picking up URGENT shifts offices need filled.
                            </Typography>
                            {/* Temporary locator alias for automation still on T2-6328 id */}
                            <span id="shift-saviour-resume-label" data-testid="shift-saviour-resume-label" hidden aria-hidden />
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <Typography sx={{display: 'flex', alignItems: 'center'}}>
                            {years === 0 && months === 0
                                ? <Spa sx={{marginRight: '0.5rem', color: 'success.main'}}/>
                                : <CalendarToday sx={{marginRight: '0.5rem', color: 'success.main'}}/>}
                            {withTempStarsString}
                        </Typography>
                    </Grid>
                    {hygienist?.type === HYGIENIST_TYPES.HYGIENIST && <Grid item xs={6}>
                        <Typography sx={{display: 'flex', alignItems: 'center'}}>
                            <ThumbUp sx={{
                                marginRight: 1,
                                color: 'secondary.main',
                                transform: hygienist?.isLeftHanded ? 'rotateY(180deg)' : ''
                            }}/>
                            {hygienist?.isLeftHanded ? 'Left-handed' : 'Right-handed'}
                        </Typography>
                    </Grid>}
                </Grid>
            </Section>
            <Section>
                <ExpandButton
                    onClick={() => setIsSkillsExpanded(!isSkillsExpanded)}
                    icon={<EmojiEvents color="primary"/>}
                    isExpanded={isSkillsExpanded}
                >
                    Skills & Qualification (these are self-reported)
                </ExpandButton>
                {isSkillsExpanded && (
                    <Paper sx={{marginTop: 2, padding: 2, backgroundColor: 'grey.100'}}>
                        <Typography fontWeight="bold" sx={{marginBottom: 1}}>
                            {hygienist?.CDHONumber?.length
                                ? <b>{(hygienist?.type ? 'Certificate #: ' : 'License #: ') + hygienist?.CDHONumber}</b>
                                : null}
                        </Typography>
                        <Typography fontWeight="bold" sx={{marginBottom: 1}}>Languages</Typography>
                        {languagesToShow?.length ? (
                            <Typography variant="body2" color="textSecondary">
                                {languagesToShow.map((lang: any) => lang.name).join(', ')}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="textSecondary">Not specified</Typography>
                        )}
                        <Typography fontWeight="bold" sx={{marginBottom: 1, marginTop: 1}}>Training & Certifications
                            received</Typography>
                        {hygienist?.skills?.map((item: SkillsProps) => (
                            <Typography key={item.label}
                                        sx={{display: 'flex', alignItems: 'center', marginBottom: 1}}>
                                <CheckCircle sx={{marginRight: 1, color: 'success.main'}}/>
                                {item.label}
                            </Typography>
                        ))}
                    </Paper>
                )}
            </Section>
            <Section>
                <ExpandButton
                    onClick={() => setIsWorkExpanded(!isWorkExpanded)}
                    icon={<Work color="primary"/>}
                    isExpanded={isWorkExpanded}
                >
                    Work Experience
                </ExpandButton>
                {isWorkExpanded && (
                    <Paper sx={{marginTop: 2, padding: 2, backgroundColor: 'grey.100'}}>
                        {hygienist?.workExperience?.map((position: PositionProps, index: number) => (
                            <div key={position.jobTitle + position.fromDate}
                                 style={{marginBottom: index === 0 ? 16 : 0}}>
                                <Typography fontWeight="bold">{position.jobTitle}</Typography>
                                <Typography variant="body2"
                                            color="textSecondary">{position.companyName}</Typography>
                                <Typography variant="body2"
                                            color="textSecondary">{dayjs(position.fromDate).format('MMM YYYY')} - {position.current ? 'Current' : dayjs(position.toDate).format('MMM YYYY')}</Typography>
                            </div>
                        ))}
                    </Paper>
                )}
            </Section>
            {hygienist?.about && <Section>
                <ExpandButton
                    onClick={() => setIsAboutExpanded(!isAboutExpanded)}
                    icon={<Person color="primary"/>}
                    isExpanded={isAboutExpanded}
                >
                    About Me
                </ExpandButton>
                {isAboutExpanded && (
                    <Paper sx={{marginTop: 2, padding: 2, backgroundColor: 'grey.100'}}>
                        <Typography>
                            {hygienist.about}
                        </Typography>
                    </Paper>
                )}
            </Section>}
            {hygienist?.completedJobs > 0 && <Section>
                <ExpandButton
                    onClick={() => setIsTempStarsHistoryExpanded(!isTempStarsHistoryExpanded)}
                    icon={<CheckCircle color="primary"/>}
                    isExpanded={isTempStarsHistoryExpanded}
                >
                    TempStars Work History
                </ExpandButton>
                {isTempStarsHistoryExpanded && (
                    <Paper sx={{marginTop: 2, padding: 2, backgroundColor: 'grey.100'}}>
                        <Grid container justifyContent="space-between" alignItems="center"
                              sx={{marginBottom: 1}}>
                            <Typography fontWeight="bold">Shifts Completed:</Typography>
                            <Typography>{hygienist?.completedJobs}</Typography>
                        </Grid>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography fontWeight="bold">Average Rating:</Typography>
                            <Typography sx={{display: 'flex', alignItems: 'center'}}>
                                {hygienist?.starScore}
                                <Star sx={{marginLeft: 0.5, color: 'warning.main'}}/>
                            </Typography>
                        </Grid>
                    </Paper>
                )}
            </Section>}
        </StyledPaper>
    );
};

export default TalentResume;
